import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Database, Cpu, Sparkles, Send, Undo2 } from "lucide-react";

export default function DwhSimulator() {
  const [count, setCount] = useState(10);
  const [pending, setPending] = useState(0);
  const [simResult, setSimResult] = useState(null);
  const [rewardResult, setRewardResult] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [refundResult, setRefundResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStats = async () => {
    const { data } = await api.get("/referrals?status=pending");
    setPending(data.length);
  };
  useEffect(() => { loadStats(); }, []);

  const simulate = async () => {
    setBusy(true);
    const { data } = await api.post("/dwh/simulate-orders", { count });
    setSimResult(data);
    await loadStats();
    setBusy(false);
  };

  const processRewards = async () => {
    setBusy(true);
    const { data } = await api.post("/dwh/process-rewards");
    setRewardResult(data);
    await loadStats();
    setBusy(false);
  };

  const sendInvites = async () => {
    setBusy(true);
    const { data } = await api.post("/wa/send-invites", {});
    setInviteResult(data);
    setBusy(false);
  };

  const simulateRefund = async () => {
    setBusy(true);
    const { data } = await api.post("/dwh/simulate-refund", {});
    setRefundResult(data);
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#1a1c19] sm:text-3xl">DWH Simulator</h1>
        <p className="text-sm text-[#5c665a]">
          Mimic the hourly PostgreSQL DWH refresh. Flow (matches the production journey):
          <strong> invites fire 48h after a referrer's delivery → friends sign up →
          their orders deliver → reward engine → payouts 14 days later.</strong>
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="admin-card">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-[#66cc33]" />
            <h3 className="font-semibold">Pending referrals</h3>
          </div>
          <div className="mt-3 text-4xl font-extrabold">{pending}</div>
          <p className="mt-1 text-xs text-[#5c665a]">Friends who signed up — awaiting a delivered order.</p>
        </div>

        <div className="admin-card">
          <div className="flex items-center gap-2">
            <Send className="h-5 w-5 text-[#ff3399]" />
            <h3 className="font-semibold">1. Send invites</h3>
          </div>
          <p className="mt-2 text-xs text-[#5c665a]">
            Post-delivery WhatsApp invites to eligible referrers.
          </p>
          <button className="admin-btn-pink mt-4 w-full" onClick={sendInvites} disabled={busy} data-testid="dwh-send-invites">
            <Send className="h-4 w-4" /> Send eligible invites
          </button>
          {inviteResult && (
            <div className="mt-3 space-y-1 rounded-lg bg-[#fff0f5] p-3 text-[11px] font-semibold text-[#c01974]">
              <div>Sent: {inviteResult.sent}</div>
              <div>Cooldown blocked: {inviteResult.cooldown_blocked}</div>
              <div>Tenure blocked: {inviteResult.tenure_blocked}</div>
              <div>Skipped: {inviteResult.skipped}</div>
            </div>
          )}
        </div>

        <div className="admin-card">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#66cc33]" />
            <h3 className="font-semibold">2. Simulate orders</h3>
          </div>
          <p className="mt-2 text-xs text-[#5c665a]">Create N delivered DWH orders for captured friends.</p>
          <div className="mt-4 flex items-center gap-2">
            <input
              type="number"
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              className="admin-input max-w-[90px]"
              data-testid="sim-count-input"
            />
            <button className="admin-btn" onClick={simulate} disabled={busy} data-testid="sim-orders-btn">
              Generate
            </button>
          </div>
          {simResult && (
            <div className="mt-3 rounded-lg bg-[#eaf8de] p-3 text-xs font-semibold text-[#3f7c21]">
              Created {simResult.created} orders {simResult.message ? `· ${simResult.message}` : ""}
            </div>
          )}
        </div>

        <div className="admin-card">
          <div className="flex items-center gap-2">
            <Cpu className="h-5 w-5 text-[#66cc33]" />
            <h3 className="font-semibold">3. Run reward engine</h3>
          </div>
          <p className="mt-2 text-xs text-[#5c665a]">Apply fraud checks + schedule payouts 14 days out.</p>
          <button className="admin-btn mt-4 w-full" onClick={processRewards} disabled={busy} data-testid="process-rewards-btn">
            Process rewards
          </button>
          {rewardResult && (
            <div className="mt-3 space-y-1 rounded-lg bg-[#eaf8de] p-3 text-xs font-semibold text-[#3f7c21]">
              <div>Processed: {rewardResult.processed}</div>
              <div>Rewarded: {rewardResult.rewarded}</div>
              <div>Flagged: {rewardResult.flagged}</div>
            </div>
          )}
        </div>
      </div>

      <div className="admin-card">
        <div className="flex items-center gap-2">
          <Undo2 className="h-5 w-5 text-amber-500" />
          <h3 className="font-semibold">Refund / cancellation branch</h3>
        </div>
        <p className="mt-2 text-xs text-[#5c665a]">
          Simulate a friend refunding their order within the 14-day review window — voids the promo, cancels the referral,
          and sends the referrer a polite cancellation message on WhatsApp.
        </p>
        <button className="admin-btn-ghost mt-3" onClick={simulateRefund} disabled={busy} data-testid="sim-refund-btn">
          <Undo2 className="h-4 w-4" /> Simulate a refund
        </button>
        {refundResult && (
          <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-700">
            {refundResult.cancelled ? `Cancelled referral ${refundResult.referral_id?.slice(0,8)}…` : refundResult.message}
          </div>
        )}
      </div>

      <div className="admin-card">
        <h3 className="font-semibold">End-to-end demo flow</h3>
        <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-[#5c665a]">
          <li>Click <strong>Send eligible invites</strong> — referrers get a WhatsApp invite with their link.</li>
          <li>Open <a className="font-semibold text-[#66cc33]" href="/">the WhatsApp simulator</a>, pick a referrer, tap <strong>[Share link]</strong>.</li>
          <li>Open the shared link in a new tab — fill phone + email on the friend landing, copy promo code.</li>
          <li>Come back here and click <strong>Generate</strong> to create the delivered DWH order.</li>
          <li>Click <strong>Process rewards</strong> → payout scheduled → go to <strong>Payouts → Run scheduled</strong>.</li>
          <li>Referrer receives paired <em>M-Pesa SMS + WhatsApp</em> confirmation with ref ID.</li>
        </ol>
      </div>
    </div>
  );
}
